import { createEffect, createSignal, lazy, onMount, Show } from 'solid-js';
import { useAppContext } from '../../app-context-provider/app-context-provider';
import clsx from 'clsx';
import { ErrorCatcher } from '../../tools/error-catcher';
import type { JSX } from 'solid-js';
import { HeroProps } from './hero.types';

type PreloadableRiveComponent = (() => JSX.Element) & {
  preload: () => Promise<{ default: () => JSX.Element }>;
};

export const Hero = (props: HeroProps) => {
    const { pageTheme, scrollValue, setScrollValue } = useAppContext();

    const [riveComponent, setRiveComponent] = createSignal<PreloadableRiveComponent | null>(null);
    const [loaded, setLoaded] = createSignal(false);
    const [hideDiv, setHideDiv] = createSignal(false);

    const [bgClasses, setbgClasses] = createSignal('h-[88svh]');
    setbgClasses(clsx(
        'h-[100svh] tablet:h-[88svh]',
        'relative',
        'm-0',
        {'hidden': hideDiv()},
    ));

    onMount(async () => {
        const rive = lazy(() => import('./responsive-hero-rive'));
        setRiveComponent(() => rive);
    });

    const handleWheelEvent = (e: any) => {
        const deltaY = e.deltaY;
        const increment = deltaY > 0 ? 1 : -1;
        const newNumber = Math.min(200, Math.max(1, scrollValue() + increment));
        setScrollValue(newNumber);
    };

    createEffect(() => {
        if (riveComponent()) {
            setTimeout(() => {
                setLoaded(true);

            }, 50);
            setTimeout(() => {
                setHideDiv(true);
            }, 400);
        }
    });

    const get = () => {
        const wrapperClasses = clsx(
            pageTheme().colors.classes,
            'h-[100svh] tablet:h-[88svh]',
            'w-full',
            'absolute',
            'text-left',
            'tablet:text-center',
            'desktop:text-center',
            'left-0',
            'right-0',
            'pr-4',
            'pl-4',
            'pt-[1.5rem]',
            'tablet:mx-auto',
            'desktop:mx-auto',
            { 'opacity-0': loaded() },
            { '!hidden': hideDiv() },
            'transition-all',
            'duration-300',
            'ease-in-out',
            'h-[88svh]',
        );
        return wrapperClasses;
    };
    const loadedClasses = clsx(
        'absolute',
        'text-center',
        'left-0',
        'right-0',
        'max-w-xl',
        'tablet:max-w-3xl',
        'desktop:max-w-4xl',
        'pl-8',
        'pr-8',
        'tablet:mx-auto',
        'desktop:mx-auto',
        'flex',
        'flex-col',
        'justify-center',
        'h-[100svh] tablet:h-[88svh]',
    );

    const h1Classes = clsx(
        'font-paytone',
        'text-accent1-300',
        'text-headerXXL',
    );

    return (
        <ErrorCatcher componentName="Hero">
            <div class={bgClasses()} onWheel={handleWheelEvent}>
                <div class={get()} />
                <div class={loadedClasses}>
                    <h1 class={h1Classes} classList={{'tablet:text-primary-100' : !!riveComponent()}}>
                        {props.largerHeading}
                    </h1>
                </div>
                <Show when={riveComponent()}>
                    {(Rive) => {
                        const Component = Rive(); 
                        return <Component />; 
                    }}
                </Show>

            </div>
        </ErrorCatcher>
    );
};

Hero.parseProps = (atts: HeroProps) => {
    // This is the raw image object coming from gutenberg. We dont want all details from it.

    const { image, button } = atts;
    const commonAtts = {
        altText: atts.altText,
        imageText: atts.imageText,
        eyebrowHeading: atts.eyebrowHeading,
        largerHeading: atts.largerHeading,
        description: atts.description,
        bottomDividerColor: atts.bottomDividerColor,
        learnMoreButton: button[0],
        labels: atts.labels,
        secondaryButton: button[1],
    };

    if (image) {
        return {
            src: image.url,
            width: image.width,
            focus: image.focus,
            overlayType: atts.overlayType,
            ...commonAtts,
        };
    } else return commonAtts;
};


